import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useTestSessionsData, useAuth, GroupMemberRole } from '@modmd/data'
import { ShowUserCode } from 'sharedComponents/ShowUserCode'
import { Box, Container } from 'components/Layout'
import Button from 'components/Button'
import Datalist from 'components/Datalist'
import {
  TestSessionsDatalist,
  PAGE_LENGTH as UPCOMING_SESSIONS_LIMIT_FOR_PAGINATION,
} from 'sharedComponents/TestSessionsDatalist'
import { ROUTES, PERSONAL_ROUTES } from 'client-portal/constants/routes'
import { PersonalPageLayout } from 'client-portal/components/PageLayout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { ConfirmationDialog } from 'components/Dialog'
import { Text } from 'components/Typography'
import { CheckUserClearanceStatus } from 'sharedComponents/CheckUserClearanceStatus'
import { startOfDay } from 'date-fns'

enum Message {
  CONFIRMATION = 'You are trying to book a personal visit. If you are part of a group, your appointment will be scheduled for you.',
}

export enum PaginationProps {
  PAGE = 'page',
}

export const PersonalTestSessions: React.VFC<RouteComponentProps> = ({ history }) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const handleCloseDialog = () => setIsDialogOpen(false)
  const handleRedirectToRequestTestSession = () => history.push(`/${ROUTES.REQUEST_TEST_SESSION}`)

  const auth = useAuth()

  const [upcomingSessionPage, setUpcomingSessionPage] = React.useState(1)
  const searchState = useSearchState<{
    [PaginationProps.PAGE]?: string
  }>()

  const commonFilter = React.useMemo(
    () => ({ userId: auth.data.User.id, isParticipant: true, userIds: [auth.data.User.id] }),
    [auth.data.User.id]
  )

  const upComingTestSessionQuery = useTestSessionsData({
    filter: {
      ...commonFilter,
      dateFrom: startOfDay(new Date()).toISOString(),
      userId: auth.data.User.id,
    },
    page: upcomingSessionPage,
  })

  const previousTestSessionQuery = useTestSessionsData({
    filter: {
      ...commonFilter,
      dateTo: startOfDay(new Date()).toISOString(),
      userId: auth.data.User.id,
    },
    page: Number(searchState.data.page || 1),
  })

  const companies = auth.data.User.CompaniesWithRole.filter(
    ({ eventClearance }) => eventClearance
  ).map((company) => company.id)

  const { groups } = auth.getUserGroupsWithRole({
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator, GroupMemberRole.Member],
    companyIds: companies,
  })

  return (
    <PersonalPageLayout>
      <Container>
        {groups.length > 0 && <CheckUserClearanceStatus groups={groups} />}
        <TestSessionsDatalist
          tableHeader={
            <Datalist.Title
              rightContent={
                <>
                  <ShowUserCode isBarcode userId={auth.data.User.id} />
                </>
              }
            >
              Upcoming Test Sessions
            </Datalist.Title>
          }
          testSessions={upComingTestSessionQuery.data?.testSessions?.testSessions || []}
          pagination={
            upComingTestSessionQuery.data &&
            upComingTestSessionQuery.data?.testSessions.pagination.total >
              UPCOMING_SESSIONS_LIMIT_FOR_PAGINATION
              ? upComingTestSessionQuery.data?.testSessions.pagination
              : undefined
          }
          columnLabels={{
            numOfPeople: null,
            company: null,
            invoiceCheck: null,
            sublocation: null,
          }}
          isLoading={upComingTestSessionQuery.loading}
          onPageChange={setUpcomingSessionPage}
          onRowClick={({ id }) =>
            history.push(`/${ROUTES.PERSONAL}/${PERSONAL_ROUTES.SESSIONS}/${id}`)
          }
        />
        <Box mt="2rem" mb="2rem">
          <TestSessionsDatalist
            tableHeader={<Datalist.Title>Previous Test Sessions</Datalist.Title>}
            testSessions={previousTestSessionQuery.data?.testSessions?.testSessions || []}
            columnLabels={{
              invoiceCheck: null,
              numOfPeople: null,
              company: null,
              sublocation: null,
            }}
            pagination={previousTestSessionQuery.data?.testSessions.pagination}
            isLoading={previousTestSessionQuery.loading}
            onPageChange={(page) => searchState.setSearchState({ page }, false)}
            onRowClick={({ id }) =>
              history.push(`/${ROUTES.PERSONAL}/${PERSONAL_ROUTES.SESSIONS}/${id}`)
            }
          />
        </Box>
        <ConfirmationDialog
          title="Schedule session"
          isOpen={isDialogOpen}
          onDismiss={handleCloseDialog}
          actions={
            <>
              <Button onClick={handleCloseDialog} colorVariant="secondary">
                Cancel
              </Button>
              <Button onClick={handleRedirectToRequestTestSession}>Continue</Button>
            </>
          }
        >
          <Text>{Message.CONFIRMATION}</Text>
        </ConfirmationDialog>
      </Container>
    </PersonalPageLayout>
  )
}
